<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import type { IPostbacksAddPayload } from '@modules/postbacks/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePostbackTypes } from '@modules/postbacks/composables';
import { TRANSLATION_GROUP_POSTBACKS } from '@modules/postbacks/constants';
import { usePostbacksCreateMutation } from '@modules/postbacks/mutations';
import {
  ADMIN_POSTBACKS_SETTINGS_ROUTE_NAME,
  AFFILIATE_POSTBACKS_SETTINGS_ROUTE_NAME,
} from '@modules/postbacks/submodules/edit/constants';
import { useUserSupervision } from '@modules/user/composables';
import { isString } from '@utils/guards';

const { getTranslation } = useI18n();
const route = useRoute();
const router = useRouter();
const { isAdminNamespace } = useCurrentNamespace();
const { isSupervision } = useUserSupervision();

const type = computed(() => (isString(route.params.type) ? route.params.type : ''));
const id = computed(() => (isString(route.params.id) ? Number(route.params.id) : undefined));

const {
  mutateAsync,
  isLoading,
} = usePostbacksCreateMutation();

const { typeOptions } = usePostbackTypes();

const formSchema = computed<FormSchema<IPostbacksAddPayload>>(() => ({
  type: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'CREATE_TYPE').value,
    options: typeOptions.value,
    value: type.value,
  },
}));

const form = ref<VFormRef>();

const isVisible = ref(false);

function onHide(): void {
  isVisible.value = false;
}

function onSubmit(): void {
  form.value?.submit();
}

function onButtonClick(): void {
  isVisible.value = true;
}

async function onFormSubmit(values: IPostbacksAddPayload): Promise<void> {
  const response = await mutateAsync({
    ...values,
    id: id.value,
  });
  onHide();

  void router.push({
    name: isAdminNamespace.value ? ADMIN_POSTBACKS_SETTINGS_ROUTE_NAME : AFFILIATE_POSTBACKS_SETTINGS_ROUTE_NAME,
    params: {
      id: id.value,
      postbackId: response.webhook.id,
      type: values.type,
    },
  });
}
</script>

<template>
  <div v-data-qa-auto-id="'PostbacksAddDialog'">
    <VButton
      :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'ADD_WEBHOOK')"
      :disabled="isSupervision"
      :loading="isLoading"
      @click="onButtonClick"
    />
    <VDialog
      :title="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'CREATE_TITLE')"
      :visible="isVisible"
      @hide="onHide"
    >
      <VForm
        ref="form"
        :schema="formSchema"
        @submit="onFormSubmit"
      />
      <template #footer>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'CREATE_CANCEL')"
          outlined
          @click="onHide"
        />
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'CREATE_YES')"
          :loading="isLoading"
          @click="onSubmit"
        />
      </template>
    </VDialog>
  </div>
</template>
